import { useCustom } from "@refinedev/core";
import { AlertTriangle, PackageSearch, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Combo } from "@/components/Combo";
import { MarkAll, MarkBar, MarkCell } from "@/components/Marks";
import { api, query } from "@/lib/api";
import { normalize, nothing, outcome } from "@/lib/boxFind";
import {
  boxWord,
  errorMessage,
  positionWord,
} from "@/lib/format";
import { pageData } from "@/lib/loaded";
import type { BoxFound, BoxRow, Boxes } from "@/lib/providers";
import {
  click,
  commaList,
  empty,
  picked,
  selectAll,
  type Selection,
} from "@/lib/select";
import { useSessionGuard } from "@/lib/useSessionGuard";

/** Сколько строк добавляет одно нажатие «показать ещё». Ровно страница API. */
const PAGE = 300;

/**
 * Короба склада, сверху — самые срочные для Wildberries.
 *
 * Раздел «Товары» отвечает на «сколько их у нас и где». Здесь вопрос
 * обратный: КАКОЙ КОРОБ ВЕЗТИ ПЕРВЫМ. Ответ — тот, что вернёт на полку ВБ
 * больше всего позиций: товар, которого на складе маркетплейса нет, не
 * продаётся вовсе, пока короб стоит у нас (решение владельца 08.09.26).
 *
 * Числа на экране РАЗНОЙ ПРИРОДЫ, и подписаны они поэтому по-разному:
 * «позиций» — сколько в коробе разных баркодов, «штук» — сколько вещей.
 * В коробах лежит в основном по одной штуке на баркод, отчего числа почти
 * совпадают, — но означают они разное, и колонки, названные одинаково, стали
 * бы неразличимы.
 *
 * Город живёт в АДРЕСЕ, а не в состоянии: ссылку «короба Самары» отправляют
 * сменщику, и открыться у него она обязана такой же.
 */
export function BoxesPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const cityParam = params.get("city") ?? "";
  // Потолок страницы — тоже в адресе: нажатое «показать ещё» обязано пережить
  // перезагрузку, иначе ссылка откроется у сменщика обрезанной.
  const limitParam = params.get("n") ?? "";

  /** Правит ОДИН ключ адреса, не трогая остальные. */
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Смена города возвращает список к первой странице: поднятый потолок
    // относился к прежнему отбору.
    if (key !== "n") next.delete("n");
    setParams(next, { replace: true });
  };

  const url = useMemo(
    () =>
      `/api/boxes${query({
        city_id: cityParam || undefined,
        limit: limitParam || undefined,
      })}`,
    [cityParam, limitParam],
  );

  const listQuery = useCustom<Boxes>({ url, method: "get" });
  useSessionGuard(listQuery.query.error);

  // Списком считается только payload, в котором ДЕЙСТВИТЕЛЬНО есть строки:
  // `result.data` у Refine существует и до загрузки — пустым объектом, и
  // обращение к `list.wb.available` на нём роняло экран целиком (поймано на
  // бою 08.09.26). Разбор ловушки — в `lib/loaded.ts`.
  const list = pageData(listQuery.result.data, "rows");
  const busy = listQuery.query.isFetching;
  const error = listQuery.query.error ? errorMessage(listQuery.query.error) : "";

  const rows = useMemo(() => list?.rows ?? [], [list]);
  // Порядок ключей на экране — он же порядок диапазона: «от первой до
  // десятой» считается по списку, каким человек его видит.
  const order = useMemo(() => rows.map((r) => r.place_id), [rows]);

  // Отметка галочками. Ключ — номер МЕСТА: список пересортируется на
  // следующем снимке остатков ВБ, и отметка по позиции переехала бы на
  // чужой короб.
  const [sel, setSel] = useState<Selection<number>>(() => empty<number>());
  // Что уехало в буфер: он невидим, и без ответа экрана нажатие кнопки
  // неотличимо от нажатия мимо.
  const [flash, setFlash] = useState<{ text: string; id: number } | null>(null);

  // Смена города меняет САМ СПИСОК, а отмечали видимое. Оставить отметку —
  // значит скопировать короба, которых на экране уже нет.
  useEffect(() => setSel(empty<number>()), [cityParam]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3000);
    return () => clearTimeout(t);
  }, [flash]);

  const chosen = picked(rows, (r) => r.place_id, sel);
  const marked = chosen.length;
  const allMarked = marked > 0 && marked === rows.length;

  const pick = (key: number, e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) =>
    // Shift, Ctrl и ⌘ здесь означают одно: «от той строки до этой». На складе
    // жмут то, что первым попало под палец, и разводить их по разным смыслам
    // значило бы ловить человека на привычке файлового менеджера.
    setSel((s) => click(s, order, key, e.shiftKey || e.ctrlKey || e.metaKey));

  /** Копирует поле отмеченных строк списком через запятую. */
  const copy = async (label: string, value: (row: BoxRow) => string) => {
    const { text, count, skipped } = commaList(chosen.map(value));
    if (count === 0) {
      setFlash({ text: `${label}: копировать нечего`, id: Date.now() });
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Буфер закрыт политикой браузера — показываем список, чтобы его
      // можно было выделить руками. Молча терять его нельзя.
      window.prompt("Скопируйте список:", text);
    }
    setFlash({
      text:
        `${label}: ${count}` +
        (skipped ? ` · без кода места ${skipped}` : ""),
      id: Date.now(),
    });
  };

  const cities = list?.cities ?? [];
  const cityName =
    cities.find((c) => String(c.city_id) === cityParam)?.name ?? "";

  // ПОИСК КОРОБА ПО НОМЕРУ (задача владельца 10.09.26). Наряд называет короб
  // номером, а открыть его было можно только глазами по списку — да ещё и в
  // пределах потолка страницы: коробов 788, а показано 300.
  const [draft, setDraft] = useState("");
  const [finding, setFinding] = useState(false);
  // Кандидаты, из которых человек выбирает сам, и причина, по которой выбирать
  // не из чего. Живут порознь: «нашлось три» и «не нашлось» — разные ответы.
  const [candidates, setCandidates] = useState<BoxRow[]>([]);
  const [miss, setMiss] = useState("");

  const find = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = normalize(draft);
    if (!code || finding) return;
    setFinding(true);
    setCandidates([]);
    setMiss("");
    try {
      const inCity = await api.get<BoxFound>(
        `/api/boxes/find${query({ code, city_id: cityParam || undefined })}`,
      );
      let rows = inCity.rows;
      // ГОРОД МОГ БЫТЬ ВЫБРАН НЕ ТОТ, и молчать об этом нельзя: короб 10956
      // зовётся в CRM «Невиномыск Невиномыск Короб 10956», а в справочнике
      // локаций стоит Ростовом. Не нашлось в выбранном городе — спрашиваем
      // склад целиком и показываем находку с её городом, а не открываем её
      // молча: человек искал в другом месте и обязан это увидеть.
      let elsewhere = false;
      if (rows.length === 0 && cityParam) {
        rows = (await api.get<BoxFound>(`/api/boxes/find${query({ code })}`)).rows;
        elsewhere = rows.length > 0;
      }
      const got = outcome(rows);
      if (got.kind === "none") setMiss(nothing(code, cityName));
      else if (got.kind === "open" && !elsewhere)
        navigate(`/place/${got.place_id}`);
      else setCandidates(rows);
    } catch (err) {
      setMiss(errorMessage(err));
    } finally {
      setFinding(false);
    }
  };

  // Срочность известна не всегда: зеркало кабинета ВБ может быть недоступно.
  // Тогда экран показывает короба и честно молчит про полку — вместо того
  // чтобы показать ноль, который читается как «дефицита нет».
  const wbOk = list?.wb.available ?? false;
  const missing = list?.totals.missing ?? 0;
  const urgent = list?.totals.urgent_boxes ?? 0;

  return (
    <div
      className="flex h-full min-h-0 flex-col p-4"
      data-component="BoxesPage"
      data-annot="короба"
    >
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[15px] font-semibold">Короба</h1>
          <p className="mt-0.5 text-[12.5px] text-fg-muted">
            {wbOk
              ? "Сверху те, что вернут на полку Wildberries больше всего позиций."
              : "Остатки Wildberries сейчас недоступны — порядок только по размеру короба."}
          </p>
        </div>

        <div data-annot="фильтр" className="flex flex-wrap items-center gap-2">
          {/* Город — единственный отбор этого экрана, и он про СКЛАД: «какой
              короб везти из Самары» — вопрос, с которого смена и начинается.
              Счётчик рядом с именем — сами короба, а не товары в них.

              Он же сужает и ПОИСК по номеру, стоящий рядом: «ростовский короб
              10956» — один вопрос, а не два. */}
          <Combo
            label="город"
            value={cityParam}
            onChange={(v) => set("city", v)}
            width={190}
            options={[
              { value: "", label: "все" },
              ...cities.map((c) => ({
                value: String(c.city_id),
                label: c.name,
                meta: String(c.boxes),
                title: `${c.name}: ${c.boxes} ${boxWord(c.boxes)}, ${c.units} шт`,
              })),
            ]}
          />

          {/* ПОИСК КОРОБА: набранное — либо номер короба (10956), либо код
              места со сканера (850900210956). Спрашивать заранее, что именно
              набрано, значило бы спрашивать то, что видно по самому набору. */}
          <form
            onSubmit={find}
            data-annot="поиск короба"
            className="flex items-center gap-2"
          >
            <label className="text-[12.5px] text-fg-muted" htmlFor="box-code">
              короб
            </label>
            <input
              id="box-code"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              inputMode="numeric"
              placeholder="номер или код места"
              title="Номер короба (10956) или код места со сканера"
              className="h-8 w-[190px] rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px] tnum focus:border-border-strong"
            />
            <button
              type="submit"
              disabled={finding}
              className="flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg disabled:opacity-60"
            >
              <Search size={13} />
              {finding ? "Ищу…" : "Открыть"}
            </button>
          </form>
        </div>
      </div>

      {/* Ответ поиска стоит ОТДЕЛЬНОЙ строкой под шапкой, а не в самом списке:
          список — это склад, а находка — ответ на набранное, и подменять им
          список значило бы терять из виду, что показано на экране. */}
      {miss ? (
        <p
          data-annot="короб не найден"
          className="mb-3 flex items-center gap-2 text-[12.5px] text-fg-muted"
        >
          <AlertTriangle size={13} className="flex-none text-warn" />
          {miss}
        </p>
      ) : null}
      {candidates.length > 0 ? (
        <div
          data-annot="кандидаты"
          className="mb-3 flex flex-wrap items-center gap-2 text-[12.5px]"
        >
          {/* Сюда попадают ДВА разных случая, и оба требуют человека: номер
              совпал у нескольких коробов либо короб нашёлся не в том городе,
              который выбран. Открыть за него — значит увести не туда. */}
          <span className="text-fg-muted">
            {candidates.length === 1
              ? "Нашёлся в другом городе:"
              : `Коробов с таким номером ${candidates.length}:`}
          </span>
          {candidates.map((one) => (
            <Link
              key={one.place_id}
              to={`/place/${one.place_id}`}
              className="rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2 py-0.5 hover:bg-bg-hover"
            >
              <span className="tnum font-medium">{one.code}</span>
              <span className="ml-1.5 text-fg-muted">
                {one.city} · {one.units} шт
              </span>
            </Link>
          ))}
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="mb-3 flex items-center gap-2 rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-3 py-2 text-[12.5px]"
        >
          <AlertTriangle size={14} className="text-err" />
          <span>{error}</span>
          <button
            type="button"
            onClick={() => listQuery.query.refetch()}
            className="ml-auto rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] hover:bg-bg-hover"
          >
            Повторить
          </button>
        </div>
      ) : null}

      {!list ? (
        <p className="text-[13px] text-fg-muted">Считаю…</p>
      ) : (
        <>
          <dl
            data-annot="сводка"
            className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-3"
            aria-busy={busy}
          >
            <Metric
              label="Коробов"
              value={list.total}
              unit={boxWord(list.total)}
              note={`${list.totals.units} шт внутри`}
              parts={cities.map((c) => ({
                key: c.city_id,
                name: c.name,
                units: c.boxes,
                active: cityParam === String(c.city_id),
                onClick: () => set("city", String(c.city_id)),
              }))}
            />
            {/* ГЛАВНОЕ ЧИСЛО РАЗДЕЛА: сколько позиций вернётся на витрину,
                если увезти всё показанное. Рядом — в скольких коробах они
                лежат: 1 175 позиций в 509 коробах читается совсем иначе, чем
                те же 1 175 в пятнадцати. */}
            <Metric
              label="Вернут на полку"
              value={wbOk ? missing : "—"}
              unit={wbOk ? positionWord(missing) : ""}
              accent
              note={
                wbOk
                  ? `в ${urgent} ${boxWord(urgent)} из ${list.total}`
                  : "зеркало кабинета ВБ недоступно"
              }
            />
            {/* Час снимка чужой полки. Не украшение: совет «вези этот короб
                первым» стоит ровно столько, сколько стоит его свежесть. */}
            <Metric
              label="Остатки ВБ"
              value={wbOk ? list.wb.synced_at.slice(11) : "—"}
              unit={wbOk ? list.wb.synced_at.slice(0, 10) : ""}
              note={wbOk ? "снимок кабинета Wildberries" : "не прочитаны"}
            />
          </dl>

          {list.rows.length === 0 ? (
            <Empty>
              {cityParam
                ? "В этом городе сейчас нет ни одного собранного короба."
                : "На складе нет ни одного короба."}
            </Empty>
          ) : (
            <>
              {/* Отмеченные короба уносят СПИСКОМ в чужую систему — в поставку
                  ВБ, в наряд кладовщику, в сообщение сменщику. Поэтому кнопки
                  копируют ровно два разных значения, и подписаны они тем, чем
                  эти значения зовут на складе. */}
              {/* Два значения короба копируются РАЗНЫМИ кнопками, потому что
                  зовут их разным: «номер» — то, чем короб называют глазами
                  (7261), «баркод» — код места, которым его берут сканером
                  (87090027261). Подменять одно другим нельзя. */}
              <MarkBar
                marked={marked > 0 ? `Отмечено ${marked} ${boxWord(marked)}` : ""}
                allMarked={allMarked}
                hint="Отметьте короба галочками: Shift или Ctrl по второй строке отметит всё между ними."
                flash={flash?.text ?? ""}
                onAll={() =>
                  setSel(allMarked ? empty<number>() : selectAll(order))
                }
                onNone={() => setSel(empty<number>())}
                actions={[
                  {
                    label: "Скопировать баркоды коробов",
                    title: "Коды мест отмеченных коробов, через запятую",
                    annot: "скопировать баркоды",
                    onClick: () =>
                      copy("Баркоды коробов", (r) => r.place_barcode),
                  },
                  {
                    label: "Скопировать номера коробов",
                    title: "Номера отмеченных коробов, через запятую",
                    annot: "скопировать номера",
                    onClick: () => copy("Номера коробов", (r) => r.code),
                  },
                ]}
              />

            <div className="min-h-0 flex-1 overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
              <table
                data-annot="таблица"
                className="w-full border-collapse text-[13px]"
              >
                <caption className="sr-only">
                  Короба склада, сверху возвращающие на полку ВБ больше позиций
                </caption>
                <thead className="hidden md:table-header-group">
                  <tr>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 w-9 border-b border-border bg-bg-elevated px-2.5 py-2 text-center"
                    >
                      <MarkAll
                        all={allMarked}
                        some={marked > 0}
                        title="Отметить все показанные короба"
                        onToggle={() =>
                          setSel(allMarked ? empty<number>() : selectAll(order))
                        }
                      />
                    </th>
                    {[
                      "Короб",
                      "Город",
                      "Позиций",
                      "Штук",
                      "Вернёт на полку",
                    ].map((title) => (
                      <th
                        key={title}
                        scope="col"
                        className={
                          "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg " +
                          (title === "Короб" || title === "Город"
                            ? "text-left"
                            : "text-right")
                        }
                      >
                        {title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="block md:table-row-group">
                  {rows.map((row) => (
                    <tr
                      key={row.place_id}
                      data-box={row.code}
                      data-marked={sel.keys.has(row.place_id) || undefined}
                      // Адрес замечания — НОМЕР короба, а не позиция в списке:
                      // «строка 396» находится на складе, «третья сверху» —
                      // нет, список пересортируется на следующем снимке ВБ.
                      data-annot={`строка ${row.code}`}
                      className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
                    >
                      <MarkCell
                        label={`Отметить короб ${row.code}`}
                        on={sel.keys.has(row.place_id)}
                        onPick={(e) => pick(row.place_id, e)}
                      />
                      <Cell label="Короб">
                        <span className="flex flex-col">
                          <Link
                            to={`/place/${row.place_id}`}
                            className="font-medium tnum text-fg hover:text-accent-text"
                          >
                            {row.code}
                          </Link>
                          {/* Код места — то, чем короб берут СКАНЕРОМ. Номером
                              его зовут глазами, кодом — руками, поэтому в
                              строке стоят оба. */}
                          <span className="text-[11px] tnum text-fg-muted">
                            {row.place_barcode || "без кода места"}
                          </span>
                        </span>
                      </Cell>
                      <Cell label="Город">{row.city}</Cell>
                      <Cell label="Позиций" align="text-right tnum">
                        {row.barcodes}
                      </Cell>
                      <Cell label="Штук" align="text-right tnum">
                        {row.units}
                      </Cell>
                      <Cell label="Вернёт на полку" align="text-right tnum">
                        <Gain row={row} />
                      </Cell>
                    </tr>
                  ))}
                </tbody>
              </table>
              {list.shown < list.total ? (
                <div className="border-t border-border p-3 text-center">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => set("n", String(list.shown + PAGE))}
                    className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-60"
                  >
                    {busy
                      ? "Гружу…"
                      : `Показать ещё ${Math.min(PAGE, list.total - list.shown)}`}
                  </button>
                  <p className="mt-1.5 text-[11.5px] text-fg-muted">
                    Показано {list.shown} из {list.total}
                  </p>
                </div>
              ) : null}
            </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Сколько позиций короб вернёт на полку — и какая это доля короба.
 *
 * Доля стоит рядом с числом, а не вместо него: «63» отвечает, сколько
 * товара оживёт, «72%» — насколько короб целиком про это. Порядок задаёт
 * первое, второе объясняет строку глазу.
 *
 * Прочерк вместо нуля, когда зеркало ВБ недоступно: ноль читался бы как
 * «дефицита нет», и человек решил бы, что везти нечего.
 */
function Gain({ row }: { row: BoxRow }) {
  if (row.missing === null)
    return <span className="text-fg-muted">—</span>;
  if (row.missing === 0)
    return <span className="text-fg-muted">0</span>;
  const share = row.barcodes ? Math.round((row.missing / row.barcodes) * 100) : 0;
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="font-semibold text-accent-text">{row.missing}</span>
      <span className="text-[11px] text-fg-muted">{share}%</span>
    </span>
  );
}

/**
 * Ячейка таблицы, которая на узком экране становится строкой «подпись —
 * значение». Тот же приём, что в списке товаров и в `PlacementTable`: второго
 * набора разметки под мобильный экран не появляется, расходиться им негде.
 */
function Cell({
  label,
  align,
  children,
}: {
  label: string;
  align?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      data-label={label}
      data-annot={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
        (align ?? "")
      }
    >
      {children}
    </td>
  );
}

/** Плитка сводки. `parts` — разложение главного числа на слагаемые. */
function Metric({
  label,
  value,
  unit,
  note,
  accent,
  parts,
}: {
  label: string;
  value: string | number;
  unit: string;
  note: string;
  accent?: boolean;
  parts?: {
    key: number;
    name: string;
    units: number;
    active: boolean;
    onClick: () => void;
  }[];
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3">
      <dt className="text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
        {label}
      </dt>
      <dd
        className={
          "mt-1 text-[24px] font-semibold tabular-nums tracking-[-0.02em] " +
          (accent ? "text-accent-text" : "")
        }
      >
        {value}
        <span className="ml-1 text-[13px] font-normal text-fg-muted">
          {unit}
        </span>
      </dd>
      {parts && parts.length > 0 ? (
        <div data-annot="города" className="mt-1.5 flex flex-wrap gap-1">
          {parts.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={p.onClick}
              title={`Только короба города ${p.name}`}
              className={
                "rounded border px-1.5 py-px text-[12px] " +
                (p.active
                  ? "border-accent/60 bg-accent-dim text-accent-text"
                  : "border-border text-fg-muted hover:border-border-strong hover:text-fg")
              }
            >
              {p.name}{" "}
              <span className="tnum font-medium text-fg">{p.units}</span>
            </button>
          ))}
        </div>
      ) : null}
      {note ? <p className="mt-1 text-[11.5px] text-fg-muted">{note}</p> : null}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
      <PackageSearch size={20} className="mx-auto mb-2 text-fg-muted" />
      {children}
    </div>
  );
}
